import { z } from "zod";
import { evmAddress, httpUrl, positiveInt, nonNegativeInt } from "./parse.js";
export const coreSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
    LOG_LEVEL: z.enum(["fatal", "error", "warn", "info", "debug", "trace", "silent"]).default("info"),
    PORT: positiveInt.default(3000),
    // Optional: error tracking. Every service calls Sentry.init({ dsn: env.SENTRY_DSN }) — the SDK
    // itself no-ops safely when this is unset (local dev, tests), so it's optional here rather than
    // required.
    SENTRY_DSN: httpUrl.optional(),
});
export const webOriginSchema = z.object({
    WEB_ORIGIN: httpUrl,
    API_PUBLIC_URL: httpUrl,
    /** Comma-separated extra origins the API's CORS layer should also accept, alongside
     *  `WEB_ORIGIN` — for a domain migration (see app.ts's cors() setup) where the old and new
     *  brand domains are briefly live at once and real visitors can land on either one.
     *  `WEB_ORIGIN` itself stays the single canonical URL used to build absolute links in emails
     *  (services/watchlist.ts, services/savedSearches.ts) and the API's own landing page — this is
     *  purely additive for the CORS allowlist, nothing else reads it. */
    ADDITIONAL_CORS_ORIGINS: z.string().optional(),
});
export const databaseSchema = z.object({
    DATABASE_URL: z.string().min(1),
    DIRECT_DATABASE_URL: z.string().min(1),
});
export const coinbaseServerSchema = z.object({
    CDP_PROJECT_ID: z.string().min(1),
    CDP_API_KEY_ID: z.string().min(1),
    CDP_API_KEY_SECRET: z.string().min(1),
    // Override for CDP's API host (see apps/api/src/lib/cdpAuth.ts) — defaults to
    // api.cdp.coinbase.com; only needed for a non-default CDP API environment.
    CDP_API_HOST: z.string().min(1).optional(),
    // Test-environment-only auth bypass for Playwright e2e (see apps/api/src/lib/cdpAuth.ts
    // E2eTestAuthValidator). Only ever consulted when NODE_ENV === "test"; unset in every other
    // environment, so it cannot silently enable a production auth bypass.
    E2E_TEST_AUTH_SECRET: z.string().optional(),
});
export const turnstileSchema = z.object({
    // Both optional and either both set or both unset in practice: unset means the feature is
    // simply off (new-account creation proceeds unchecked, same as before this was added — see
    // apps/api/src/lib/turnstile.ts's NoopTurnstileVerifier), matching how SENTRY_DSN degrades.
    // The site key is public by design (served via GET /config/public, embedded in the sign-in
    // page); the secret key must never leave the API service.
    TURNSTILE_SITE_KEY: z.string().min(1).optional(),
    TURNSTILE_SECRET_KEY: z.string().min(1).optional(),
});
/**
 * A single fixed operator account, wholly separate from the CDP-authenticated end-user identity
 * system — the admin dashboard is a platform-operator tool, not a `User` row, so it needs no
 * wallet and isn't subject to any of the abuse-prevention gating built for end-user accounts.
 * `ADMIN_PASSWORD_HASH` is a bcrypt hash (never the plaintext); `ADMIN_SESSION_SECRET` signs the
 * stateless session token issued on login (see apps/api/src/lib/adminAuth.ts) so no server-side
 * session store is needed and it works across multiple Railway replicas without shared state.
 */
export const adminSchema = z.object({
    ADMIN_USERNAME: z.string().min(1).default("wickedmod"),
    ADMIN_PASSWORD_HASH: z.string().min(1),
    ADMIN_SESSION_SECRET: z.string().min(32, "must be at least 32 characters"),
});
/**
 * Transactional email via Resend, used for outbound app-generated mail — currently just the
 * public contact form (see apps/api/src/routes/contact.ts), but the sender abstraction
 * (lib/email.ts) is written generally enough for any future email need. Optional and degrades
 * to a no-op sender when unset, same pattern as `turnstileSchema`/`SENTRY_DSN` — a deployment
 * that hasn't configured Resend yet just doesn't send mail rather than failing to boot.
 * `RESEND_FROM_EMAIL` must be an address on a domain verified in the Resend dashboard, or
 * sends will fail at request time (not at startup, since that's only checked by Resend itself).
 */
export const emailSchema = z.object({
    RESEND_API_KEY: z.string().min(1).optional(),
    RESEND_FROM_EMAIL: z.string().min(1).default("MarketOnBase <support@marketonbase.com>"),
});
export const chainSchema = z.object({
    BASE_CHAIN_ID: positiveInt,
    BASE_RPC_URL: httpUrl,
    BASE_EXPLORER_URL: httpUrl,
    CHAIN_CONFIRMATIONS: nonNegativeInt.default(3),
    CHAIN_DEPLOYMENT_BLOCK: nonNegativeInt,
    ESCROW_CONTRACT_ADDRESS: evmAddress,
    USDC_CONTRACT_ADDRESS: evmAddress,
    ARBITRATOR_ADDRESS: evmAddress,
    TREASURY_ADDRESS: evmAddress,
    PLATFORM_FEE_BPS: positiveInt.default(1000),
    MIN_LISTING_PRICE_USDC_BASE_UNITS: positiveInt.default(100_000),
    CDP_PAYMASTER_URL: httpUrl.optional(),
});
export const storageSchema = z.object({
    S3_ENDPOINT: httpUrl,
    S3_REGION: z.string().min(1).default("auto"),
    S3_FORCE_PATH_STYLE: z
        .enum(["true", "false"])
        .default("true")
        .transform((v) => v === "true"),
    S3_ACCESS_KEY_ID: z.string().min(1),
    S3_SECRET_ACCESS_KEY: z.string().min(1),
    S3_QUARANTINE_BUCKET: z.string().min(1),
    S3_PRODUCTION_BUCKET: z.string().min(1),
    S3_EVIDENCE_BUCKET: z.string().min(1),
    UPLOAD_URL_TTL_SECONDS: positiveInt.default(300),
    DOWNLOAD_URL_TTL_SECONDS: positiveInt.default(60),
    MAX_UPLOAD_BYTES: positiveInt.default(500 * 1024 * 1024),
});
export const clamavSchema = z.object({
    CLAMAV_HOST: z.string().min(1),
    CLAMAV_PORT: positiveInt.default(3310),
    CLAMAV_CONNECT_TIMEOUT_MS: positiveInt.default(5000),
    CLAMAV_SCAN_TIMEOUT_MS: positiveInt.default(60_000),
});
/**
 * Encrypts account-transfer credentials at rest (see apps/api/src/lib/accountCredentialCrypto.ts,
 * apps/api/src/services/accountTransfers.ts) — unlike LicenseKey.code, this holds a real
 * third-party account login, not a one-time activation string, so it's AES-256-GCM-encrypted
 * rather than stored plain. Required as exactly 64 hex characters (32 raw bytes) — generate with
 * `openssl rand -hex 32`. Rotating this key orphans any not-yet-revealed credential row (it can
 * no longer be decrypted), so treat it like any other long-lived secret: set once, back up
 * securely, don't rotate casually.
 */
export const accountTransferSchema = z.object({
    ACCOUNT_CREDENTIAL_ENCRYPTION_KEY: z
        .string()
        .regex(/^[0-9a-fA-F]{64}$/, "must be exactly 64 hex characters (32 bytes) — openssl rand -hex 32"),
    // The real Google account sellers invite as Brand Account Owner for a YOUTUBE ACCOUNT_TRANSFER
    // handoff (see apps/web's AccountTransferPanel) — optional so a deployment that hasn't set this
    // up yet still runs, it just falls back to generic "our Google account" copy instead of naming
    // a concrete address for the seller to invite. Public (not a secret): mirrored into
    // GET /config/public and shown directly in the seller/buyer-facing UI.
    YOUTUBE_TRANSFER_GOOGLE_ACCOUNT: z.string().email().optional(),
});
/**
 * Optional, best-effort public-stat verification for ACCOUNT_TRANSFER listings (Phase 3 — see
 * services/platformStats.ts). Both degrade to "verification skipped" when unset, same pattern as
 * SENTRY_DSN, the TURNSTILE_ pair, and RESEND_API_KEY above — a deployment with neither configured
 * still works, it just never shows a "platform-verified" badge on account listings.
 *
 * YOUTUBE_DATA_API_KEY must be an *unrestricted-by-referrer* key (or IP-restricted, never
 * HTTP-referrer-restricted) — this is called server-to-server from Railway, which never sends a
 * browser Referer header, so a referrer-locked key (the default Google Cloud Console suggests for
 * "browser" key types) fails every call with API_KEY_HTTP_REFERRER_BLOCKED. Scope it to the
 * YouTube Data API v3 via "API restrictions" instead, which is server-safe.
 *
 * X_API_CONSUMER_KEY/X_API_CONSUMER_SECRET are the App's OAuth 1.0a API Key/Secret from the X
 * Developer Portal, not a Bearer token directly — services/platformStats.ts exchanges them for an
 * App-only Bearer token via the OAuth2 client_credentials flow at call time (cached in memory,
 * re-exchanged on a 401) rather than requiring a separately-issued, separately-rotated token.
 */
export const platformStatsSchema = z.object({
    YOUTUBE_DATA_API_KEY: z.string().min(1).optional(),
    X_API_CONSUMER_KEY: z.string().min(1).optional(),
    X_API_CONSUMER_SECRET: z.string().min(1).optional(),
});
// API service: everything except ClamAV (workers own scanning).
export const apiEnvSchema = coreSchema
    .merge(webOriginSchema)
    .merge(databaseSchema)
    .merge(coinbaseServerSchema)
    .merge(chainSchema)
    .merge(storageSchema)
    .merge(turnstileSchema)
    .merge(adminSchema)
    .merge(emailSchema)
    .merge(accountTransferSchema)
    .merge(platformStatsSchema);
// Chain worker: DB + chain, no storage/Coinbase-auth/ClamAV needed.
export const chainWorkerEnvSchema = coreSchema.merge(databaseSchema).merge(chainSchema);
// Scanner worker: DB + storage + ClamAV, no chain/Coinbase needed.
export const scannerWorkerEnvSchema = coreSchema
    .merge(databaseSchema)
    .merge(storageSchema)
    .merge(clamavSchema);
// Public shape safe to return from GET /api/v1/config/public and to mirror in VITE_* build config.
export const publicChainConfigSchema = z.object({
    chainId: z.number().int().positive(),
    chainName: z.string(),
    rpcUrl: z.string().url(),
    explorerUrl: z.string().url(),
    escrowContractAddress: evmAddress,
    usdcContractAddress: evmAddress,
    platformFeeBps: z.number().int().positive(),
    minListingPriceBaseUnits: z.string(),
    confirmations: z.number().int().nonnegative(),
    // Undefined when TURNSTILE_SITE_KEY isn't configured — the sign-in page simply skips
    // rendering the widget in that case (see apps/web SignInPage/TurnstileWidget).
    turnstileSiteKey: z.string().optional(),
    // Polled by the frontend (see usePublicConfig) so a site-wide maintenance takeover can render
    // without a deploy — toggled from the admin dashboard, backed by the MaintenanceMode DB row.
    maintenanceMode: z.boolean(),
    maintenanceMessage: z.string().nullable(),
    // Undefined when YOUTUBE_TRANSFER_GOOGLE_ACCOUNT isn't configured — see accountTransferSchema.
    youtubeTransferGoogleAccount: z.string().email().optional(),
});
//# sourceMappingURL=schema.js.map